<script setup lang="ts">
    import {
        CoachingFormAnswerType,
        ICoachingFormQuestion,
    } from '@/js/services/api/coachingForm/types';
    import { __ } from '@/js/stores/expressions';
    import { XIcon } from '@lucide/vue';
    import IconButton from '../IconButton.vue';

    const question = defineModel<ICoachingFormQuestion>('question', { required: true });
    defineProps<{
        readOnlyLabels?: boolean;
    }>();
</script>

<template>
    <div class="grid grid-cols-[5.25rem_1fr_2.5rem] gap-2 items-center">
        <span class="block text-overline">{{ __('points') }}</span>
        <span class="block text-overline">{{ __('answer') }}</span>
        <span />
        <template
            v-for="(answer, answerIndex) in question.answers.filter(
                (answer) => !answer.notApplicable
            )"
            :key="answerIndex"
        >
            <input
                :id="'answer-points-' + answer.id"
                v-model.number="answer.points"
                type="tel"
                :aria-label="
                    __('coaching-form-answer-points-number', {
                        number: String(answerIndex + 1),
                    })
                "
                placeholder="0"
                class="TextInput"
                :readonly="
                    [CoachingFormAnswerType.Interval, CoachingFormAnswerType.Text].includes(
                        question.answerType
                    )
                "
            />
            <input
                :id="'answer-label-' + answer.id"
                v-model="answer.label"
                :aria-label="__('coaching-form-answer-number', { number: String(answerIndex + 1) })"
                type="text"
                class="TextInput"
                :readonly="readOnlyLabels"
            />
            <IconButton
                type="button"
                class="text-gray-medium hover:bg-gray-lightest hover:text-error"
                area-size="size-8"
                size="size-4"
                :aria-label="
                    __('remove') +
                    ' ' +
                    __('coaching-form-answer-number', { number: String(answerIndex + 1) })
                "
                @click="
                    question.answers.splice(
                        question.answers.findIndex((a) => a.id === answer.id),
                        1
                    )
                "
            >
                <XIcon class="size-4" />
            </IconButton>
        </template>

        <template v-if="question.notApplicable">
            <input
                :id="'answer-not-applicable-points-' + question.id"
                type="text"
                value="-"
                class="TextInput"
                readonly
            />
            <input
                :id="'answer-not-applicable-label-' + question.id"
                v-model="question.notApplicableLabel"
                :aria-label="__('coaching-form-answer-number', { number: __('not-applicable') })"
                type="text"
                class="TextInput"
                :placeholder="__('not-applicable-short')"
            />
            <div />
        </template>
    </div>
</template>
